import { useCallback, useEffect, useMemo, useState } from "react";
import { DataTableNew } from "@/components/shared/DataTable";
import { Card } from "@/components/ui/Card";
import DateRangeFilter, { dateRangeParams } from "@/components/shared/DateRangeFilter";
import { PlatformService } from "@/services/platformService";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import useLiveResource from "@/hooks/useLiveResource";
import DetailModal from "../components/DetailModal";

// Fleet-wide event log. Unlike the other 3 tabs, this one works with no
// tenant selected (shows every SA event); picking a tenant scopes it via
// targetTenantId, per the frontend reference doc.
const PlatformSaTab = ({ tenantId }) => {
  const [eventType, setEventType] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [silentFetch, setSilentFetch] = useState(false);

  useEffect(() => {
    if (!silentFetch) return;
    const timer = setTimeout(() => setSilentFetch(false), 200);
    return () => clearTimeout(timer);
  }, [silentFetch]);

  // New platform events (another SuperAdmin's action, a fleet-wide batch op)
  // otherwise only appear here on the next manual page load.
  useLiveResource({
    keys: "audit_log",
    onChange: () => setSilentFetch(true),
  });

  const handleView = (row) => {
    setSelectedEvent(row);
    setDetailOpen(true);
  };

  const extraParams = useMemo(
    () => ({
      ...(tenantId && { targetTenantId: tenantId }),
      ...(eventType && { eventType }),
      ...dateRangeParams(range),
    }),
    [tenantId, eventType, range],
  );

  // This endpoint doesn't take sortBy/sortOrder — DataTableNew still sends
  // sort_by/sort_order but FastAPI silently ignores unbound query params.
  const api = useCallback((params) => PlatformService.fetchPlatformAuditLogs(params), []);

  const COLUMNS = useMemo(
    () => [
      {
        key: "createdAt",
        label: "Created at",
        render: (v) => (v ? new Date(v).toLocaleString() : "—"),
      },
      { key: "eventType", label: "Event type" },
      {
        key: "targetTenantId",
        label: "Target tenant",
        render: (v) => <span className="break-all text-xs">{v || "—"}</span>,
      },
      { key: "outcome", label: "Outcome" },
      {
        key: "errorText",
        label: "Error",
        render: (v) => v || "—",
      },
      {
        key: "view",
        label: "Actions",
        render: (_, row) => (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              handleView(row);
            }}
          >
            <Eye size={16} />
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-2.5">
      <Card className="flex flex-wrap items-center gap-2 p-2.5">
        <Input
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          placeholder="Event type (e.g. TENANT_DB_REGISTERED)"
          aria-label="Filter by event type"
          className="w-auto bg-background-tertiary px-3 py-[7px] text-xs min-w-[220px]"
        />
        <DateRangeFilter name="platform-sa" from={range.from} to={range.to} onChange={setRange} />
      </Card>

      <Card className="p-0 bg-table-heading border-border-primary">
        <DataTableNew
          columns={COLUMNS}
          api={api}
          extraParams={extraParams}
          sortable={false}
          dataPath="items"
          silentFetch={silentFetch}
          paginationLabel="events"
        />
        <DetailModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          loading={false}
          title="Platform Audit Event"
          fields={[
            { label: "Event Type", value: selectedEvent?.eventType },
            { label: "Outcome", value: selectedEvent?.outcome },
            { label: "Target Tenant", value: selectedEvent?.targetTenantId },
            { label: "Actor User ID", value: selectedEvent?.actorUserId },
            { label: "Tenant Database ID", value: selectedEvent?.targetTenantDatabaseId },
            {
              label: "Created At",
              value: selectedEvent?.createdAt ? new Date(selectedEvent.createdAt).toLocaleString() : "—",
            },
            { label: "Correlation ID", value: selectedEvent?.correlationId },
            { label: "Error", value: selectedEvent?.errorText },
          ]}
          jsonSections={[{ label: "Payload", value: selectedEvent?.payload }]}
        />
      </Card>
    </div>
  );
};

export default PlatformSaTab;
